import React, {useEffect, useMemo, useState} from 'react'
import {makeStyles} from '@material-ui/core';
import PropTypes from 'prop-types';
import {connect} from 'react-redux'
import SearchBar from './components/SearchBar'
import Tr from 'translations/Translations';
import DemandsService from 'services/DemandsService';
import RouterPath from 'libs/RouterPath';
import moment from 'moment';
import SimpleTable from 'components/complex/simpleTable/SimpleTable';
import usePathParams from 'hooks/usePathParams'
import Loading from 'components/Loading';
import {LABELS as ProcedureStatusLabels} from 'constants/ProcedureStatus';
import Pagination from 'components/Pagination';
import DemandService from 'services/DemandService';
import {ProcedureTypeTr} from 'constants/ProcedureType';
import WorkableIconDeleteButton from 'components/button/WorkableIconDeleteButton';
import Confirm from "components/popup/confirm/Confirm";
import WorkableIconForwardButton from "components/button/WorkableIconForwardButton";
import useSearch from "hooks/useSearch";
import {useDidUpdateEffect} from "hooks/useDidUpdateEffect";
import DateFormats from "constants/DateFormats";
import WorkableIconDuplicateButton from "components/button/WorkableIconDuplicateButton";

const useStyles = makeStyles(theme => ({
    root: {
        margin: -theme.spacing(2),
        marginTop: 0,
    },
    numberColumn: {
        width: 100,
        wordBreak: 'break-all'
    },
    typeColumn: {
        width: 140
    },
    dateColumn: {
        width: 150,
        textAlign: "center"
    },
    statusColumn: {
        width: 100,
    },
    button: {
        width: 50,
        padding: '0 !important',
    },
    oneLine: {
        ...theme.typography.cutLines(1),
    },
    cell: {
        padding: theme.spacing(1),
    }
}));

const rp = RouterPath.myProcedure

function Started(
    {
        resources = {},
    }) {
    const classes = useStyles();

    const {
        // paginator,
        //totalCount,
        items,
    } = resources;


    const [loading, setLoading] = useState(false);
    const [openDeleteId, setOpenDeleteId] = useState(0);
    const [openCloneId, setOpenCloneId] = useState(0);

    let params = usePathParams()
    const {searchData, totalCount, setTotalCount, setPaginationConfig, setSearchConfig, setSortConfig,}
        = useSearch({
        search: {
            params,
            filters: {
                state: ''
            }
        },
        sort: {
            column: 'dateInserted',
            direction: 'desc',
        }
    });

    const setFilters = (filters) => {
        setSearchConfig({...searchData.search, filters});
    }

    useDidUpdateEffect(() => {
        setSearchConfig({...searchData.search, params});
    }, [params]);

    useDidUpdateEffect(() => {
        if (resources?.totalCount !== undefined) {
            setTotalCount(resources.totalCount);
        }
    }, [resources?.totalCount]);

    useEffect(() => {
        return () => {
            DemandsService.clear();
        }
    }, []);

    useEffect(() => {
        fetchData()
        // eslint-disable-next-line
    }, [searchData])

    const handleSortChange = (id) => {
        let direction = 'desc';
        const active = searchData.sort.column === id;
        if (active) {
            direction = searchData.sort.direction === 'asc' ? 'desc' : 'asc';
        }

        setSortConfig({
            column: id,
            direction
        })
    }

    const fetchData = () => {
        setLoading(true);
        const {search, pagination, sort} = searchData;
        const {page, pageSize} = pagination;
        return DemandsService.get(page, pageSize, {
            ...search.filters,
            ...search.params,
        }, sort).then(() => setLoading(false));
    }

    const createDeleteButton = (onClick, {actions}) => {
        if (actions.delete) {
            return <WorkableIconDeleteButton onClick={onClick}/>
        }
    }
    const createCloneButton = (onClick, {actions}) => {
        if (actions.clone) {
            return <WorkableIconDuplicateButton onClick={onClick}/>
        }
    }

    const handleEnterDemand = (item) => {
        const {actions} = item;
        if (actions.stepOne) {
            const href = RouterPath.createPath(rp.steps.MP_1_ID, {id: item.identity});
            RouterPath.redirect(href)
        } else if (actions.stepTwo) {
            const href = RouterPath.createPath(rp.steps.MP_2_ID, {id: item.identity});
            RouterPath.redirect(href)
        } else if (actions.stepThree) {
            const href = RouterPath.createPath(rp.steps.MP_3_ID, {id: item.identity});
            RouterPath.redirect(href)
        } else if (actions.stepFour) {
            const href = RouterPath.createPath(rp.steps.MP_4_ID, {id: item.identity});
            RouterPath.redirect(href)
        } else if (actions.oldSystemUrl) {
            window.location.href = actions.oldSystemUrl.url;
        }
    }

    const handleDelete = (id = openDeleteId) => {
        setOpenDeleteId(0);
        DemandService.deleteDemand(id)
            .then(() => fetchData())
    }

    const handleClone = (id = openCloneId) => {
        setOpenCloneId(0);
        DemandService.clone(id);
    }

    const getSortProps = (id) => {
        return {
            active: searchData.sort.column === id,
            direction: searchData.sort.direction,
            onClick: () => {
                handleSortChange(id)
            }
        }
    }
    const hasClone = useMemo(() => !!items && !!items.find(x => !!x.actions.clone), [items]);
    const hasDelete = useMemo(() => !!items && !!items.find(x => !!x.actions.delete), [items]);

    const getHeaders = () => {
        if (!items) return;
        const headers = [];
        headers.push(Tr('demand_number'));
        headers.push({
            value: Tr('name'),
            sort: getSortProps('name')
        });
        headers.push(Tr('type'));
        headers.push({
            value: Tr('publish_data'),
            sort: getSortProps('procedureStartDate')
        });
        headers.push({
            value: Tr('deadline_for_offers'),
            sort: getSortProps('stageEndDate')
        });
        headers.push(Tr('status'));
        if (hasDelete) {
            headers.push('');
        }
        if (hasClone) {
            headers.push('');
        }
        headers.push('');
        return headers;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const headers = useMemo(getHeaders, [items]);

    const getHeaderClasses = () => {
        if (!items) return;
        const headers = [];
        headers.push(classes.numberColumn);
        headers.push(undefined);
        headers.push(classes.typeColumn);
        headers.push(classes.dateColumn);
        headers.push(classes.dateColumn);
        headers.push(classes.statusColumn);
        if (hasDelete) {
            headers.push(classes.button);
        }
        if (hasClone) {
            headers.push(classes.button);
        }
        headers.push(classes.button);

        return headers;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const headerClasses = useMemo(getHeaderClasses, [items]);

    const getRows = () => {
        if (!items) return;
        return items.map((item) => {
            const row = [];
            row.push(item.number);
            row.push(item.name);
            row.push(ProcedureTypeTr[item.procedureType]);
            row.push(item.datePublish ? moment(item.datePublish).format(DateFormats.DATETIME_FORMAT) : "--");
            row.push(item.deadlineForOffers ? moment(item.deadlineForOffers).format(DateFormats.DATETIME_FORMAT) : "--");
            row.push(ProcedureStatusLabels[item.state]);
            if (hasDelete) {
                row.push(createDeleteButton(() => setOpenDeleteId(item.identity), item));
            }
            if (hasClone) {
                row.push(createCloneButton(() => setOpenCloneId(item.identity), item));
            }
            row.push(<WorkableIconForwardButton onClick={()=> handleEnterDemand(item)} />);
            return row;
        });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const rows = useMemo(getRows, [items]);

    if (!items) return <Loading/>;

    return (
        <>
            <SearchBar
                setFilters={setFilters}
                filters={searchData.search.filters}
            />
            {loading ?
                <Loading/>
                :
                <SimpleTable
                    headers={headers}
                    items={rows}
                    columnClasses={headerClasses}
                    cellClassName={classes.cell}
                />
            }

            {/* <GalleryList /> */}
            <Pagination config={searchData.pagination} setConfig={setPaginationConfig} totalCount={totalCount}/>

            <Confirm
                open={Boolean(openDeleteId)}
                onConfirm={() => handleDelete()}
                onCancel={() => setOpenDeleteId(0)}
                confirmText="delete_popup_text1"
                confirmText2="delete_popup_text2"
            />
            <Confirm
                open={Boolean(openCloneId)}
                onConfirm={() => handleClone()}
                onCancel={() => setOpenCloneId(0)}
                confirmText="confirm_clone_demand"
            />
        </>
    )
}

Started.propTypes = {
    classes: PropTypes.any,
}

Started.defaultProps = {}

export default connect((state) => ({
    resources: state.resources.demands
}), {})(Started);